import { useCallback, useEffect, useState } from 'react';
import { Box, Group, LoadingOverlay, Stack, Text, Title } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { SectionConfig, UpdateDataExportSchemaConfigRequest } from './types';
import useDataExportSchemaConfigs from './hooks/useDataExportSchemaConfigs';
import useDataExportSchemaConfigById from './hooks/useDataExportSchemaConfigById';
import useSaveDataExportSchemaConfig from './hooks/useSaveDataExportSchemaConfig';
import useResetDataExportSchemaConfig from './hooks/useResetDataExportSchemaConfig';
import useCreateDataExportSchemaConfig from './hooks/useCreateDataExportSchemaConfig';
import useDeleteDataExportSchemaConfig from './hooks/useDeleteDataExportSchemaConfig';
import ConfigListPanel from './components/ConfigListPanel';
import ConfigEditorPanel from './components/ConfigEditorPanel';

export default function DataExportConfigPage() {
  const { data: configs, isLoading: isLoadingConfigs } = useDataExportSchemaConfigs();
  const [selectedConfigId, setSelectedConfigId] = useState<string | null>(null);

  const { data: selectedConfig, isLoading: isLoadingConfig } =
    useDataExportSchemaConfigById(selectedConfigId);
  const { saveConfig, isLoading: isSaving } = useSaveDataExportSchemaConfig();
  const { resetConfig, isLoading: isResetting } = useResetDataExportSchemaConfig();
  const { createConfig } = useCreateDataExportSchemaConfig();
  const { deleteConfig, isLoading: isDeleting } = useDeleteDataExportSchemaConfig();

  const [sections, setSections] = useState<SectionConfig[]>([]);
  const [displayName, setDisplayName] = useState('');
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (configs?.length && !selectedConfigId) {
      const defaultConfig = configs.find(c => c.isDefault);
      setSelectedConfigId(defaultConfig?.id ?? configs[0].id);
    }
  }, [configs, selectedConfigId]);

  useEffect(() => {
    if (selectedConfig) {
      setSections(selectedConfig.sections);
      setDisplayName(selectedConfig.displayName);
      setHasChanges(false);
    }
  }, [selectedConfig]);

  const handleSectionsChange = useCallback((updated: SectionConfig[]) => {
    setSections(updated);
    setHasChanges(true);
  }, []);

  const handleDisplayNameChange = useCallback((name: string) => {
    setDisplayName(name);
    setHasChanges(true);
  }, []);

  const handleSave = useCallback(async () => {
    if (!selectedConfigId) return;

    const request: UpdateDataExportSchemaConfigRequest = {
      displayName,
      sections: sections.map(s => ({
        tableTag: s.tableTag,
        visible: s.visible,
        displayName: s.displayName,
        fields: s.fields.map(f => ({
          entityFieldName: f.entityFieldName,
          visible: f.visible,
          label: f.label,
        })),
      })),
    };
    try {
      await saveConfig(selectedConfigId, request);
      setHasChanges(false);
      notifications.show({
        title: 'Saved',
        message: 'Configuration saved',
        color: 'green',
      });
    } catch (e) {
      notifyAndLogError('Error', 'Failed to save configuration', e);
    }
  }, [selectedConfigId, displayName, sections, saveConfig]);

  const handleReset = useCallback(async () => {
    try {
      await resetConfig();
      setHasChanges(false);
      notifications.show({
        title: 'Reset',
        message: 'Default configuration reset to defaults',
        color: 'blue',
      });
    } catch (e) {
      notifyAndLogError('Error', 'Failed to reset configuration', e);
    }
  }, [resetConfig]);

  const handleCreateConfig = useCallback(async () => {
    try {
      const defaultSections = selectedConfig?.sections ?? [];
      const result = await createConfig({
        displayName: 'New configuration',
        sections: defaultSections.map(s => ({
          tableTag: s.tableTag,
          visible: s.visible,
          displayName: s.displayName,
          fields: s.fields.map(f => ({
            entityFieldName: f.entityFieldName,
            visible: f.visible,
            label: f.label,
          })),
        })),
      });
      if (result?.id) {
        setSelectedConfigId(result.id);
      }
      notifications.show({
        title: 'Created',
        message: 'New configuration created',
        color: 'green',
      });
    } catch (e) {
      notifyAndLogError('Error', 'Failed to create configuration', e);
    }
  }, [selectedConfig, createConfig]);

  const handleDeleteConfig = useCallback(
    async (configId: string) => {
      try {
        await deleteConfig(configId);
        if (selectedConfigId === configId) {
          const defaultConfig = configs?.find(c => c.isDefault);
          setSelectedConfigId(defaultConfig?.id ?? null);
        }
        notifications.show({
          title: 'Deleted',
          message: 'Configuration deleted',
          color: 'green',
        });
      } catch (e) {
        notifyAndLogError('Error', 'Failed to delete configuration', e);
      }
    },
    [selectedConfigId, configs, deleteConfig],
  );

  const isDefault = selectedConfig?.isDefault ?? false;

  return (
    <Box pos="relative">
      <LoadingOverlay visible={isLoadingConfigs} />
      <Stack gap="md">
        <Box>
          <Title order={3}>Data export configuration</Title>
          <Text size="sm" c="dimmed">
            Manage multiple configurations. Drag sections and fields to reorder. Toggle visibility
            and edit field labels.
          </Text>
        </Box>
        <Group align="flex-start" gap="lg" wrap="nowrap">
          <Box style={{ width: 280, flexShrink: 0 }}>
            <ConfigListPanel
              configs={configs ?? []}
              selectedConfigId={selectedConfigId}
              onSelectConfig={setSelectedConfigId}
              onCreateConfig={handleCreateConfig}
              onDeleteConfig={handleDeleteConfig}
              isDeleting={isDeleting}
            />
          </Box>
          {selectedConfigId && (
            <ConfigEditorPanel
              displayName={displayName}
              onDisplayNameChange={handleDisplayNameChange}
              sections={sections}
              onSectionsChange={handleSectionsChange}
              onSave={handleSave}
              onReset={handleReset}
              isSaving={isSaving}
              isResetting={isResetting}
              isLoading={isLoadingConfig}
              hasChanges={hasChanges}
              isDefault={isDefault}
            />
          )}
        </Group>
      </Stack>
    </Box>
  );
}
